/**
 * @file ProductTypeSection.tsx
 *
 * First section of the product form: choose whether the product is physical
 * (shipped) or digital (downloadable). Rendered as a Gravity radio group of
 * {@link RichOptionItem}s; the choice drives whether the downloadable-files
 * section appears and how the save payload sets `virtual`.
 */

import { RichOptionItem } from '@group.one/react-gravity';
import { useIntl } from 'react-intl';
import { adminMessages } from '../../../i18n/messages';
import type { ProductType } from '../product-form-types';
import styles from '../ProductForm.module.css';

interface ProductTypeSectionProps {
  value: ProductType;
  onChange: (value: ProductType) => void;
}

/**
 * Renders the physical/digital product-type chooser.
 *
 * @param props - The current type and a change handler.
 *
 * @returns The product-type section.
 */
export function ProductTypeSection({ value, onChange }: ProductTypeSectionProps) {
  const intl = useIntl();
  return (
    <div className={styles.typeOptions} data-testid="product-form-type">
      <RichOptionItem
        value="physical"
        name="productType"
        title={intl.formatMessage(adminMessages.formPhysical)}
        description={intl.formatMessage(adminMessages.formPhysicalDescription)}
        condensed
        checked={value === 'physical'}
        onChange={() => onChange('physical')}
      />
      <RichOptionItem
        value="digital"
        name="productType"
        title={intl.formatMessage(adminMessages.formDigital)}
        description={intl.formatMessage(adminMessages.formDigitalDescription)}
        condensed
        checked={value === 'digital'}
        onChange={() => onChange('digital')}
      />
    </div>
  );
}
